import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FilterService, GridComponent } from '@syncfusion/ej2-angular-grids';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, Subject, switchMap, takeUntil } from 'rxjs';
import { InventoryService } from 'src/app/services/inventory.service';
import { PosSettingsService } from 'src/app/services/pos-settings.service';
import { ProductService } from 'src/app/services/Product.service';
import { VendorService } from 'src/app/services/Vendor.service';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { VendorSearchComponent } from 'src/app/Shared/vendor-search/vendor-search.component';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { PercentageFormatterDirective } from 'src/app/Shared/percentageFormatter.directive';
import { CurrencyFormatterDirective } from 'src/app/Shared/currencyFormatter.directive';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { EActionTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { GeneralService } from 'src/app/services/general.service';
import { ReportsService } from 'src/app/services/report.service';
import { IReportResponse } from 'src/app/Shared/model/ReportsModel';
import { OperationRentalService } from 'src/app/services/Rentals.service';
import Swal from 'sweetalert2';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-stock-details',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, WordReplacePipe, VendorSearchComponent,
    PercentageFormatterDirective, CurrencyFormatterDirective, UserPermissionHandlerDirective, TooltipModule, LoaderComponent],
  providers: [FilterService, DatePipe, CurrencyPipe, DecimalPipe],
  standalone: true,
  templateUrl: './ops-stock-details.html',
  styleUrl: './ops-stock-details.scss',
})

export class OpsStockDetails implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  public loaderIcon: Promise<boolean>;
  isCollapsed: boolean = true;
  @ViewChild('gridStockDetails', { static: false }) public gridStockDetails: GridComponent;
  @ViewChild(VendorSearchComponent) vendorSearch!: VendorSearchComponent;
  VendorFilterOption = {
    All: true,
    Current: false,
    Inactive: false,
    PageModel: {
      pageCount: 1,
      pageSize: 20
    }
  };
  searchFieldOptions: { id: number; name: string }[] = [
    { id: 1, name: "Category" },
    { id: 2, name: "Vendor Stock ID" },
    { id: 3, name: "Item Description" },
  ];
  stockData: any[] = [];
  selectedSearchField: number = 1;
  searchValue: string = '';
  fromDate: Date | null = null;
  toDate: Date | null = null;
  inventoryStockForm!: FormGroup;
  inventoryReturnForm!: FormGroup;
  inventoryImageForm!: FormGroup;
  barcodeTemplateForm!: FormGroup;
  marketVendorId?: string;
  vendorId?: number;
  vendorName?: string;
  productCategory: { CatSeqCategoryId: number, CatTxtCategoryDescr: string }[] = [];
  searchByDate: boolean = false;
  selectedRow: any = null;
  barcodeTemplates: any[] = [];
  selectAllPrintBarcode: boolean = false;
  selectedStockToPrint: number[] = [];
  private vendorIdInput$ = new Subject<string>();
  quantityValidator = (maxStock: number): ValidatorFn => {
    return (control: AbstractControl) => {
      const value = control.value;
      if (value == null) return { required: true };
      if (value <= 0) return { min: true };
      if (value > maxStock) return { max: true };
      return null;
    };
  };
  originalInventoryRow: any = null;
  isUpdateItemSaveEnabled = false;
  logoPreviewUrl: string = './assets/images/gallery.svg';
  private searchValue$ = new Subject<string>();
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.StockDetails, Action: EActionTypes.VIEW };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.StockDetails, Action: EActionTypes.EDIT };
  reportUrl;
  marketId: any;
  showReturnAllReason: boolean = false;
  returnAllReason: string = '';
  minExpiryDate: Date = new Date();
  maxExpiryDate: Date = new Date();
  marketCurrentDate!: Date;
  editedCountMap: { [barcodeId: number]: number } = {};
  editedCountString: string = '';
  uniqueSessionId: string = '';
  thermalBarcodePrintMap: { [barcodeId: number]: any } = {};
  private inventoryImageVersion = Date.now();

  constructor(
    private fb: FormBuilder,
    private generalService: GeneralService,
    private inventoryService: InventoryService,
    private productService: ProductService,
    private posInventoryService: PosSettingsService,
    private vendorService: VendorService,
    private rentalService: OperationRentalService,
    private reportService: ReportsService,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private router: Router,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private decimalPipe: DecimalPipe,
  ) { }

  ngOnInit(): void {
    this.initForms();
    this.loadMarketCurrentDate();
    this.getReportUrl();
    const tabId = sessionStorage.getItem('tabId');
    const sessionKey = `UniqueSessionId-${tabId}`;
    this.uniqueSessionId = localStorage.getItem(sessionKey);
    this.rentalService.getDefaultMarketId().subscribe((res: any) => {
      if (res) {
        this.marketId = res;
      }
    });
    this.loadProductCategories();
    this.loadBarcodeTemplateSettings();
    this.loadDefaultBarcodeTemplate();
    this.setupVendorIdListener();
    this.setupSearchValueListener();
    this.inventoryStockForm.valueChanges
      .pipe(takeUntil(this.destroy$)).subscribe(() => this.checkForStockChanges());
    this.barcodeTemplateForm.get('BarcodeTemplateId')?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((id: number) => {

        if (!id) return;
        setTimeout(() => {
          this.loadBarcodeTemplateSettings();
        }, 200);

        const selectedTemplate =
          this.barcodeTemplates.find(x => x.BarcodeTemplateSettingId == id)
          || this.barcodeTemplates[0]; // fallback

        if (selectedTemplate) {
          this.barcodeTemplateForm.patchValue({
            TemplateName: selectedTemplate.TemplateName,
            MaxDisplayColumns: selectedTemplate.MaxDisplayColumns ?? 3,
            DisplayWithLabels: selectedTemplate.DisplayWithLabels ?? true,
            DisplayVendorId: selectedTemplate.ShowVendorId ?? true,
            DisplayStockId: selectedTemplate.ShowStockId ?? true,
            DisplayItemName: selectedTemplate.ShowItemName ?? true,
            DisplayPrice: selectedTemplate.ShowPrice ?? true
          }, { emitEvent: false });
        }
      });
  }

  initForms(): void {
    this.inventoryStockForm = this.fb.group({
      BarcodeId: [null, Validators.required],
      StockInwardNumber: [null, [Validators.required]],
      CategoryId: [null, [Validators.required]],
      CategoryName: [null],
      StockId: [null, [Validators.required]],
      ItemDescription: [null, [Validators.required, Validators.maxLength(100)]],
      Rate: [null, [Validators.required]],
      CurrentStock: [null, [Validators.required]],
      Quantity: [null],
      CommissionRate: [null],
      SoldQuantity: [null],
      Count: [null],
      PurchaseCost: [null, [Validators.required]],
      ReorderPoint: [null],
      VendorDiscount: [0, [Validators.required]],
      NoTax: [false],
      StockReceivedDate: [null],
      VendorId: [null],
      Notes: [null],
      UserId: [null],
      ItemSerialNumber: [null],
      InventoryImageUrl: [null]
    });

    this.inventoryReturnForm = this.fb.group({
      BarcodeId: [null, Validators.required],
      ReturnQuantity: [null,],
      ReturnReason: [null, [Validators.required, this.noWhitespaceValidator(), Validators.maxLength(50)]],
    });

    this.inventoryImageForm = this.fb.group({
      BarcodeId: [null, Validators.required],
      ItemSerialNumber: [null],
      InventoryImageName: [null, Validators.required],
      UploadedInventoryImageContent: [null],
      DeleteFlag: [false]
    });

    this.barcodeTemplateForm = this.fb.group({
      BarcodeTemplateId: ['', Validators.required],
      TemplateName: ['', Validators.required],
      MaxDisplayColumns: [3],
      DisplayWithLabels: [true],
      DisplayVendorId: [true],
      DisplayStockId: [true],
      DisplayItemName: [true],
      DisplayPrice: [true],
      BarcodeIds: ['', Validators.required],
      UsedLabel: [0],
    });

  }

  loadMarketCurrentDate(): void {
    this.generalService.GetMarketCurrentTime().pipe(takeUntil(this.destroy$)).subscribe({
      next: (marketTime: string) => {
        const marketDate = marketTime.split('T')[0];
        this.marketCurrentDate = this.toLocalDate(marketDate);
        this.maxExpiryDate = this.marketCurrentDate;
        this.minExpiryDate = this.marketCurrentDate;
      },
      error: (err) => {
        console.error("Error fetching market time", err);
      }
    });
  }

  getReportUrl() {
    this.generalService.getReportUrl().subscribe((response: any) => {
      this.reportUrl = response;
    });
  }

  invokeReport(obj) {
    var openInNewTab = '_blank';
    this.loaderIcon = Promise.resolve(true);
    this.reportService.generateReport(obj).subscribe((response: IReportResponse) => {
      if (response.Success) {
        window.open(this.reportUrl + `report?id=` + response.ReportRequestId,
          openInNewTab, 'width=' + (screen.availWidth - 5) + ',height=' + (screen.availHeight - 5));
        this.loaderIcon = Promise.resolve(false);
      } else {
        console.log('Error on generating Operational Report');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onReportView() {

    const formValue = this.barcodeTemplateForm.value;
    const reportName = this.getReportName(formValue.TemplateName);

    // Thermal Templates
    if (reportName?.toLowerCase() === 'standard' || reportName?.toLowerCase() === 'butterfly') {

      const thermalBarcodePrintData = Object.keys(this.thermalBarcodePrintMap)
        .map(id => `${id}:${this.thermalBarcodePrintMap[+id]}`).join(',');

      this.printThermalBarcodeLabels(thermalBarcodePrintData, reportName);

      return;
    }

    const obj: any = {
      ReportName: reportName,
      ReportParameters: {},
      MarketId: this.marketId
    };

    obj.ReportParameters["MarketId"] = Number(this.marketId);
    obj.ReportParameters["VendorId"] = Number(this.vendorId);
    obj.ReportParameters["BarcodeIds"] = formValue.BarcodeIds;
    obj.ReportParameters["UsedLabel"] = Number(formValue.UsedLabel);
    //Template Settings
    obj.ReportParameters["MaxDisplayColumns"] = Number(formValue.MaxDisplayColumns);
    obj.ReportParameters["DisplayWithLabels"] = formValue.DisplayWithLabels ? 1 : 0;
    obj.ReportParameters["DisplayVendorId"] = formValue.DisplayVendorId ? 1 : 0;
    obj.ReportParameters["DisplayStockId"] = formValue.DisplayStockId ? 1 : 0;
    obj.ReportParameters["DisplayItemName"] = formValue.DisplayItemName ? 1 : 0;
    obj.ReportParameters["DisplayPrice"] = formValue.DisplayPrice ? 1 : 0;

    obj.ReportParameters["IsConsignmentInventory"] = Number(0);
    obj.ReportParameters["BarcodeQtyOverrides"] = this.editedCountString;
    //console.log("Barcode report parameters", obj.ReportParameters);
    this.invokeReport(obj);
  }

  getReportName(templateName: string): string {
    const name = templateName?.toLowerCase()?.trim();

    if (name?.includes('5160'))
      return 'BarcodeAvery5160';

    if (name?.includes('5195'))
      return 'BarcodeAvery5195';

    if (name?.includes('6570'))
      return 'BarcodeAvery6570';

    if (name?.includes('standard'))
      return 'standard';

    if (name?.includes('butterfly'))
      return 'butterfly';

    return 'BarcodeAvery5160';
  }

  setupVendorIdListener(): void {
    this.vendorIdInput$.pipe(debounceTime(1500), distinctUntilChanged(),
      switchMap((id: string) => {
        if (!id) {
          this.vendorName = '';
          this.vendorId = undefined;
          return [];
        }
        return this.vendorService.getVendorInfoByMarketVendorId(id);
      }),
      takeUntil(this.destroy$)
    )
      .subscribe({
        next: (res: any) => {
          if (res && res.VendorName && res.VendorId) {
            this.vendorName = res.VendorName;
            this.vendorId = res.VendorId;
            this.refreshInventoryData();
          } else {
            this.vendorName = '';
            this.vendorId = undefined;
            Swal.fire({
              icon: 'warning',
              title: 'Warning',
              text: 'There are no Vendors with this ID.',
              confirmButtonText: 'OK'
            });
            this.marketVendorId = ''
          }
        },
        error: () => {
          this.vendorName = '';
          this.vendorId = undefined;
        }
      });
  }

  setupSearchValueListener() {
    this.searchValue$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.refreshInventoryData();
    });
  }

  onMarketVendorIdChange(value: string) {
    this.vendorId = value ? Number(value) : undefined;
    this.stockData = [];
    this.clearSelectedRow();
    this.vendorIdInput$.next(value);
  }

  clearSelectedRow() {
    this.selectedRow = null;
    this.selectAllPrintBarcode = false;
    this.inventoryReturnForm.reset();
    this.inventoryImageForm.reset();
    this.logoPreviewUrl = './assets/images/gallery.svg';
  }

  refreshInventoryData() {

    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Date Range',
        text: 'From Date cannot be greater than To Date.',
        confirmButtonText: 'OK'
      });

      this.fromDate = this.marketCurrentDate;
      this.toDate = this.marketCurrentDate;
      this.minExpiryDate = this.marketCurrentDate;
      return;
    }

    this.minExpiryDate = this.fromDate || this.marketCurrentDate;

    if (!this.vendorId) return;

    this.loadInventoryStock();
  }

  onSearchValueChange(value: string) {
    this.searchValue = value;
    this.searchValue$.next(value);
  }

  onSearchByDateChanged() {
    if (this.searchByDate) {
      this.fromDate = this.marketCurrentDate;
      this.toDate = this.marketCurrentDate;
    } else {
      this.fromDate = null;
      this.toDate = null;
    }
    this.selectedRow = null;
    this.refreshInventoryData();
  }

  loadProductCategories(): void {
    this.productService.GetAllProductCategories().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (Array.isArray(res)) {
          this.productCategory = res
            .filter((item: any) => item.CatBlnCategoryInactive === false || item.CatBlnCategoryInactive === null)
            .map(item => ({
              CatSeqCategoryId: item.CatSeqCategoryId,
              CatTxtCategoryDescr: item.CatTxtCategoryDescr
            }));
        } else {
          console.warn('Unexpected response:', res);
          this.productCategory = [];
        }
      },
      error: err => {
        console.error('Error loading categories', 'Error', this.sharedHTMLSettings.ToasterConfig);
        this.productCategory = [];
      }
    });
  }

  loadBarcodeTemplateSettings(): void {
    this.inventoryService.getBarcodeTemplateSettings().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.barcodeTemplates = res
      },
      error: (err) => {
        console.error('Error fetching barcode template settings', err);
      }
    });
  }

  loadDefaultBarcodeTemplate(): void {
    this.loaderIcon = Promise.resolve(true);
    this.posInventoryService.getPosSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          this.loaderIcon = Promise.resolve(false);
          const templateName = settings?.OtherSettings?.DefaultBarcodeTemplate?.toLowerCase()?.trim();

          const matchedTemplate = this.barcodeTemplates.find(x =>
            x.TemplateName?.toLowerCase()?.trim() === templateName
          );
          // fallback to first item if no match
          const selectedTemplate = matchedTemplate || this.barcodeTemplates[0];

          if (selectedTemplate) {
            this.barcodeTemplateForm.patchValue({
              BarcodeTemplateId: selectedTemplate.BarcodeTemplateSettingId,
              TemplateName: selectedTemplate.TemplateName,
              MaxDisplayColumns: selectedTemplate.MaxDisplayColumns ?? 3,
              DisplayWithLabels: selectedTemplate.DisplayWithLabels ?? true,
              DisplayVendorId: selectedTemplate.ShowVendorId ?? true,
              DisplayStockId: selectedTemplate.ShowStockId ?? true,
              DisplayItemName: selectedTemplate.ShowItemName ?? true,
              DisplayPrice: selectedTemplate.ShowPrice ?? true,
              UsedLabel: 0
            });
          }
        },
        error: (err) => {
          this.loaderIcon = Promise.resolve(false);
          console.error('Failed to load POS settings', err)
        }
      });
  }

  loadInventoryStock() {
    if (!this.vendorId) {
      this.toastr.warning("Please select a Vendor first");
      return;
    }

    const value = this.searchValue?.trim() || undefined;
    let fromDateStr: string | undefined;
    let toDateStr: string | undefined;

    if (this.searchByDate && this.fromDate && this.toDate) {
      fromDateStr = this.datePipe.transform(this.fromDate, "yyyy-MM-dd");
      toDateStr = this.datePipe.transform(this.toDate, "yyyy-MM-dd");
    }

    this.loaderIcon = Promise.resolve(true);
    this.inventoryService.getInventoryStockByVendor(
      this.vendorId,
      this.selectedSearchField,
      value,
      fromDateStr,
      toDateStr
    ).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.stockData = (res || []).map(row => ({
            ...row,
            printBarcode: false,
            OriginalCount: row.Count
          }));
          this.inventoryReturnForm.reset();
          this.inventoryImageForm.reset();
          this.editedCountMap = {};
          this.thermalBarcodePrintMap = {};
          this.editedCountString = '';
          this.loaderIcon = Promise.resolve(false);
        },
        error: (err) => {
          this.loaderIcon = Promise.resolve(false);
          console.error(err);
          this.toastr.error($localize`Failed to load inventory`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
        }
      });
  }

  buildEditedCountString(): void {
    this.editedCountString = Object.keys(this.editedCountMap)
      .map(barcodeId =>
        `${barcodeId}:${this.editedCountMap[+barcodeId]}`
      )
      .join(',');

    //console.log(this.editedCountString);
  }

  onRowSelected(args: any) {
    if (!args?.data) return;
    this.selectedRow = args.data;
    this.patchAllForms(args.data);
  }

  onRowSelecting(args: any) {
    const clickedRow = args.data;
    if (
      this.selectedRow &&
      this.selectedRow.BarcodeId === clickedRow.BarcodeId
    ) {
      args.cancel = true;
      this.selectedRow = null;
      this.gridStockDetails.clearSelection();
      this.inventoryStockForm.reset();
      this.inventoryReturnForm.reset();
      this.inventoryImageForm.reset();

      this.logoPreviewUrl = './assets/images/gallery.svg';
    }
  }

  onSelectRow(row: any, event: any) {

    event.stopPropagation();

    const index = this.stockData.findIndex(
      x => x.BarcodeId === row.BarcodeId
    );

    const isSameRow =
      this.selectedRow &&
      this.selectedRow.BarcodeId === row.BarcodeId;

    if (isSameRow) {

      this.selectedRow = null;
      this.gridStockDetails.clearSelection();
      this.inventoryStockForm.reset();
      this.inventoryReturnForm.reset();
      this.inventoryImageForm.reset();

      this.logoPreviewUrl = './assets/images/gallery.svg';

    } else {
      this.gridStockDetails.selectRow(index);
    }
  }

  isRowSelected(row: any): boolean {
    if (!this.gridStockDetails) return false;
    const selectedRecords = this.gridStockDetails.getSelectedRecords() || [];
    return selectedRecords.some(
      (x: any) => x.BarcodeId === row.BarcodeId
    );
  }

  hasSelectedRow(): boolean {
    if (!this.gridStockDetails) return false;
    return this.gridStockDetails.getSelectedRecords().length > 0;
  }

  formatCountInput(row: any): void {
    if (row.Count === null || row.Count === undefined) {
      row.Count = row.OriginalCount;
      return;
    }
    // remove non-digits
    let value = row.Count.toString().replace(/\D/g, '');
    // remove leading zeros
    value = value.replace(/^0+/, '');
    if (value === '') {
      value = '0';
    }
    let numericValue = Number(value);
    // if (numericValue > row.OriginalCount) {
    //   numericValue = row.OriginalCount;
    // }

    row.Count = numericValue;

    if (numericValue !== row.OriginalCount) {
      this.editedCountMap[row.BarcodeId] = numericValue;
    } else {
      delete this.editedCountMap[row.BarcodeId];
    }

    this.buildEditedCountString();
    this.buildThermalBarcodePrintData();
  }

  allowOnlyCountNumbers(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    // allow only digits
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  patchAllForms(row: any) {

    this.selectedRow = row;
    this.originalInventoryRow = { ...row };

    this.inventoryStockForm.patchValue({
      BarcodeId: row.BarcodeId || null,
      StockInwardNumber: row.StockInwardNumber || null,
      CategoryId: row.CategoryId || null,
      StockId: row.StockId || '',
      ItemDescription: row.ItemDescription || '',
      Rate: row.Rate || 0,
      CurrentStock: row.CurrentStock || 0,
      PurchaseCost: row.PurchaseCost || 0,
      ReorderPoint: row.ReorderPoint || 0,
      VendorDiscount: row.VendorDiscount || 0,
      CommissionRate: row.CommissionRate || 0,
      NoTax: row.NoTax ?? false,
      Notes: row.Notes || ''
    });

    this.inventoryReturnForm.patchValue({
      BarcodeId: row.BarcodeId,
      ReturnQuantity: row.CurrentStock,
      ReturnReason: ''
    });

    this.inventoryImageForm.patchValue({
      BarcodeId: row.BarcodeId,
      ItemSerialNumber: row.ItemSerialNumber,
      UploadedInventoryImageContent: null,
      DeleteFlag: false,
    });

    this.logoPreviewUrl = row.InventoryImageUrl
      ? `${row.InventoryImageUrl}?v=${this.inventoryImageVersion}`
      : './assets/images/gallery.svg';

    const currentStock = row.CurrentStock;
    this.inventoryReturnForm.get('ReturnQuantity')?.setValidators([
      Validators.required,
      this.quantityValidator(currentStock)
    ]);
    this.inventoryReturnForm.get('ReturnQuantity')?.updateValueAndValidity();

    this.isUpdateItemSaveEnabled = false;

    setTimeout(() => this.formatInventoryInitialValues(), 0);
  }

  checkForStockChanges() {
    if (!this.originalInventoryRow) return;

    const formValue = this.inventoryStockForm.value;

    const categoryChanged = formValue.CategoryId !== this.originalInventoryRow.CategoryId;
    const descChanged = (formValue.ItemDescription || '').trim() !== (this.originalInventoryRow.ItemDescription || '');
    const rateChanged = parseFloat(formValue.Rate) !== parseFloat(this.originalInventoryRow.Rate);
    const purchaseCostChanged = parseFloat(formValue.PurchaseCost) !== parseFloat(this.originalInventoryRow.PurchaseCost);
    const reorderPointChanged = parseFloat(formValue.ReorderPoint) !== parseFloat(this.originalInventoryRow.ReorderPoint);
    const vendorDiscountChanged = parseFloat(formValue.VendorDiscount) !== parseFloat(this.originalInventoryRow.VendorDiscount);
    const commissionChanged = parseFloat(formValue.CommissionRate) !== parseFloat(this.originalInventoryRow.CommissionRate);
    const noTaxChanged = !!formValue.NoTax !== !!this.originalInventoryRow.NoTax;

    this.isUpdateItemSaveEnabled =
      categoryChanged ||
      descChanged ||
      rateChanged ||
      purchaseCostChanged ||
      reorderPointChanged ||
      vendorDiscountChanged ||
      commissionChanged ||
      noTaxChanged
  }

  updateInventoryItem() {
    if (this.inventoryStockForm.invalid) {
      this.inventoryStockForm.markAllAsTouched();
      return;
    }

    const rate = parseFloat(this.inventoryStockForm.value.Rate) || 0;
    const purchaseCost = parseFloat(this.inventoryStockForm.value.PurchaseCost) || 0;

    if (purchaseCost > rate) {
      Swal.fire({
        title: 'Confirmation',
        text: 'Purchase cost is greater than rate. Are you sure you want to proceed?',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then((result) => {
        if (result.isConfirmed) {
          this.proceedUpdate();
        }
      });

      return;
    }

    this.proceedUpdate();
  }

  proceedUpdate() {
    const payload = {
      BarcodeId: this.inventoryStockForm.value.BarcodeId,
      StockInwardNumber: this.inventoryStockForm.value.StockInwardNumber,
      CategoryId: this.inventoryStockForm.value.CategoryId,
      ItemDescription: (this.inventoryStockForm.value.ItemDescription || '').trim(),
      Rate: parseFloat(this.inventoryStockForm.value.Rate) || 0,
      PurchaseCost: parseFloat(this.inventoryStockForm.value.PurchaseCost) || 0,
      ReorderPoint: this.inventoryStockForm.value.ReorderPoint !== null && this.inventoryStockForm.value.ReorderPoint !== ''
        ? Number(this.inventoryStockForm.value.ReorderPoint)
        : 0,
      VendorDiscount: parseFloat(this.inventoryStockForm.value.VendorDiscount) || 0,
      CommissionRate: parseFloat(this.inventoryStockForm.value.CommissionRate) || 0,
      NoTax: !!this.inventoryStockForm.value.NoTax,
      Notes: (this.inventoryStockForm.value.Notes || '').trim()
    };

    this.loaderIcon = Promise.resolve(true);
    this.inventoryService.updateInventoryStockDetails(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res.Success) {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.success(res.Message || $localize`Invenntory Item updated successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);
          this.closeModalById('updateStockModal');
          this.loadInventoryStock();
        } else {
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error(res.Message || 'Update failed');
        }
      },
      error: (err) => {
        this.loaderIcon = Promise.resolve(false);
        console.error(err);
        this.toastr.error('Error updating inventory item');
      }
    });
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value && control.value.trim().length === 0) {
        return { whitespace: true };
      }
      return null;
    };
  }

  saveReturnItem() {
    if (this.inventoryReturnForm.invalid || !this.selectedRow) {
      this.inventoryReturnForm.markAllAsTouched();
      return;
    }

    const returnQty = this.inventoryReturnForm.value.ReturnQuantity;
    const currentStock = this.selectedRow.CurrentStock;

    Swal.fire({
      title: $localize`Confirm Return`,
      text: $localize`From current stock ${currentStock}, do you want to return ${returnQty} items?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, Return`,
      cancelButtonText: $localize`Cancel`,
    }).then((result) => {
      if (result.isConfirmed) {
        const payload = {
          BarcodeId: this.selectedRow.BarcodeId,
          ReturnQuantity: returnQty,
          ReturnReason: (this.inventoryReturnForm.value.ReturnReason || '').trim()
        };

        this.loaderIcon = Promise.resolve(true);
        this.inventoryService.returnInventoryItem(payload).pipe(takeUntil(this.destroy$)).subscribe({
          next: (res) => {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.success(
              res.Message || $localize`Inventory Item updated successfully`,
              $localize`Success`,
              this.sharedHTMLSettings.ToasterConfig
            );

            this.closeModalById('returnModal');
            this.selectedRow = null;
            this.inventoryReturnForm.reset();
            this.loadInventoryStock();
          },
          error: (err) => {
            this.loaderIcon = Promise.resolve(false);
            console.error(err);
            this.toastr.error(
              err?.Message || $localize`Error returning item`,
              $localize`Error`,
              this.sharedHTMLSettings.ToasterConfig
            );
          }
        });
      }
    });
  }

  onConfirmReturnAll() {
    this.showReturnAllReason = true;
  }

  submitReturnAll(): void {
    if (!this.vendorId) {
      this.toastr.info("Please select a Vendor first", 'Info', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    if (!this.returnAllReason || this.returnAllReason.trim().length === 0) {
      this.toastr.info("Reason is required", 'Info', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const barcodeIds = this.stockData.filter(item => item.BarcodeId).map(item => item.BarcodeId);

    if (barcodeIds.length === 0) {
      this.toastr.warning("No items found to return");
      return;
    }

    this.loaderIcon = Promise.resolve(true);
    this.inventoryService.returnAllInventoryItemsByVendorId(this.vendorId!, this.returnAllReason, barcodeIds).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res.Success) {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.success(
              `Returned ${barcodeIds.length} stock items successfully`,
              'Success',
              this.sharedHTMLSettings.ToasterConfig
            );

            this.closeReturnAllModal();
            this.loadInventoryStock();
          } else {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.error(res.Message || 'Return failed');
          }
        },
        error: (err) => {
          this.loaderIcon = Promise.resolve(false);
          console.error(err);
          this.toastr.error('Something went wrong,Return failed', 'Error', this.sharedHTMLSettings.ToasterConfig);
        }
      });
  }

  closeReturnAllModal() {
    this.showReturnAllReason = false;
    this.inventoryReturnForm.get('ReturnAllReason')?.reset();
    this.closeModalById('returnAllModal');
  }

  onSelectAllPrintBarcode(): void {
    const checked = this.selectAllPrintBarcode;

    this.stockData.forEach(row => {
      row.printBarcode = checked;
    });

    if (checked) {
      this.selectedStockToPrint = this.stockData
        .map(row => Number(row.BarcodeId))
        .filter(id => id > 0);
    } else {
      this.selectedStockToPrint = [];
    }

    this.updateBarcodeIds();
    this.buildThermalBarcodePrintData();

    this.stockData = [...this.stockData];

    this.stockData.forEach(row => {
      const editedValue = this.editedCountMap[row.BarcodeId];
      if (editedValue !== undefined) {
        row.Count = editedValue;
      }
    });
  }

  onPrintLabelChange(row: any, event: any): void {
    const checked = event.target.checked;
    const barcodeId = Number(row.BarcodeId);

    const actualRow = this.stockData.find(x => x.BarcodeId === barcodeId);
    if (actualRow) {
      actualRow.printBarcode = checked;
    }

    if (checked) {
      if (!this.selectedStockToPrint.includes(barcodeId)) {
        this.selectedStockToPrint.push(barcodeId);
      }
    } else {
      this.selectedStockToPrint = this.selectedStockToPrint.filter(
        id => id !== barcodeId
      );
    }

    this.selectAllPrintBarcode =
      this.stockData.length > 0 &&
      this.stockData.every(x => x.printBarcode === true);

    this.updateBarcodeIds();
    this.buildThermalBarcodePrintData();
  }

  updateBarcodeIds(): void {
    const sortedIds = [...this.selectedStockToPrint].sort((a, b) => a - b);

    const barcodeString = sortedIds.join(',');

    this.barcodeTemplateForm.patchValue({
      BarcodeIds: barcodeString
    });

    //console.log('BarcodeIds:', this.barcodeTemplateForm.get('BarcodeIds')?.value);
  }


  buildThermalBarcodePrintData(): void {
    this.thermalBarcodePrintMap = {};

    this.stockData.filter(x => x.printBarcode)
      .forEach(x => {
        const editedCount = this.editedCountMap[x.BarcodeId];
        this.thermalBarcodePrintMap[x.BarcodeId] = editedCount !== undefined ? editedCount : x.Count;
      });

    //console.log(this.thermalBarcodePrintMap);
  }

  closeModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      const modalInstance = window['bootstrap'].Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
    }

    const backdrops = document.getElementsByClassName('modal-backdrop');
    while (backdrops.length > 0) {
      backdrops[0].parentNode?.removeChild(backdrops[0]);
    }

    document.body.classList.remove('modal-open');

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  openModalById(modalId: string) {
    const modalEl = document.getElementById(modalId);
    if (modalEl) {
      let modal = window['bootstrap'].Modal.getInstance(modalEl);
      if (!modal) {
        modal = new window['bootstrap'].Modal(modalEl, { backdrop: 'static', keyboard: false });
      }
      modal.show();
    }
  }

  addLogoClick() {
    const el = document.getElementById('imageInventoryFileInput') as HTMLElement;
    if (el) {
      el.click();
    }
  }

  addInventoryImage(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('Only JPG and PNG files are allowed.');
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (file.type === 'image/jpeg') {
          // Already JPG → no conversion needed
          const base64Content = (reader.result as string).split(',')[1];
          this.logoPreviewUrl = reader.result as string;
          this.inventoryImageForm.patchValue({
            InventoryImageName: file.name,
            UploadedInventoryImageContent: base64Content,
            DeleteFlag: false
          });
          input.value = '';
        } else {
          // Convert PNG → JPG
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d')!;
            canvas.width = img.width;
            canvas.height = img.height;

            ctx.drawImage(img, 0, 0);

            const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9); // JPG output
            const base64Content = jpgDataUrl.split(',')[1];
            this.logoPreviewUrl = jpgDataUrl;

            this.inventoryImageForm.patchValue({
              InventoryImageName: file.name.replace(/\.[^/.]+$/, '') + '.jpg',
              UploadedInventoryImageContent: base64Content,
              DeleteFlag: false
            });
            input.value = '';
          };
          img.src = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  removeInventoryImage() {
    this.inventoryImageForm.patchValue({
      InventoryImageName: '',
      UploadedInventoryImageContent: null,
      DeleteFlag: true
    });
    this.logoPreviewUrl = './assets/images/gallery.svg';
    const fileInput = document.getElementById('logoFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  saveInventoryImage(): void {

    const payload = {
      BarcodeId: this.inventoryImageForm.value.BarcodeId || 0,
      ItemSerialNumber: this.inventoryImageForm.value.ItemSerialNumber || null,
      InventoryImageName: this.inventoryImageForm.value.InventoryImageName || '',
      UploadedInventoryImageContent: this.inventoryImageForm.value.UploadedInventoryImageContent || null,
      DeleteFlag: this.inventoryImageForm.value.DeleteFlag || false
    };

    if (!payload.UploadedInventoryImageContent && !payload.DeleteFlag) {
      console.error('No image selected or remove action not flagged');
      return;
    }

    const actionText = payload.DeleteFlag ? 'delete' : 'add';
    Swal.fire({
      title: `Are you sure you want to ${actionText} this Inventory Image?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${actionText}`,
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {

        this.closeModalById('addImageModal');
        this.loaderIcon = Promise.resolve(true);
        this.inventoryService.saveInventoryImage(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              if (res.Success) {
                this.loaderIcon = Promise.resolve(false);
                this.toastr.success(
                  $localize`${res.Message}`,
                  $localize`Success`,
                  this.sharedHTMLSettings.ToasterConfig
                );

                this.inventoryImageVersion = Date.now();
                this.logoPreviewUrl = res.InventoryImageUrl
                  ? `${res.InventoryImageUrl}?v=${this.inventoryImageVersion}`
                  : './assets/images/gallery.svg';

                this.loadInventoryStock();
                this.inventoryImageForm.patchValue({
                  UploadedInventoryImageContent: null,
                  DeleteFlag: false
                });
              } else {
                this.loaderIcon = Promise.resolve(false);
                this.toastr.error(
                  $localize`Error saving Inventory IMage`,
                  $localize`Error`,
                  this.sharedHTMLSettings.ToasterConfig
                );
              }
            },
            error: (err) => {
              this.loaderIcon = Promise.resolve(false);
              console.error('Failed to save inventory image', err);
              this.toastr.error(
                $localize`Failed to save inventory image`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              );
            }
          });
      }
    });
  }

  get isDefaultInventoryImage(): boolean {
    return this.logoPreviewUrl === './assets/images/gallery.svg';
  }

  get canSaveInventoryImage(): boolean {
    return !!this.inventoryImageForm.get('UploadedInventoryImageContent')?.value
      || this.inventoryImageForm.get('DeleteFlag')?.value;
  }

  get canRemoveInventoryImage(): boolean {
    return !this.isDefaultInventoryImage
      && !this.inventoryImageForm.get('DeleteFlag')?.value;
  }

  openVendorSearchModal() {

    this.vendorSearch?.resetSearchFields();

    const modalEl = document.getElementById('vendorSearchModal');
    if (modalEl) {
      let vendorModal = window['bootstrap'].Modal.getInstance(modalEl);
      if (!vendorModal) {
        vendorModal = new window['bootstrap'].Modal(modalEl, {
          backdrop: 'static',
          keyboard: false
        });
      }
      vendorModal.show();
    }
  }

  onVendorSelected(event: any) {
    if (event) {
      this.marketVendorId = event.marketVendorId;
      this.vendorId = event.vendorId;
      this.vendorName = event.vendorName;
      this.clearSelectedRow();
      this.refreshInventoryData();
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  formatInventoryInitialValues() {
    const form = this.inventoryStockForm;

    const rate = form.get('Rate')?.value || 0;
    const purchaseCost = form.get('PurchaseCost')?.value || 0;
    const vendorDiscount = form.get('VendorDiscount')?.value || 0; // percentage
    const commissionRate = form.get('CommissionRate')?.value || 0; // percentage

    const rateInput = document.querySelector<HTMLInputElement>('input[formControlName="Rate"]');
    const purchaseCostInput = document.querySelector<HTMLInputElement>('input[formControlName="PurchaseCost"]');
    const vendorDiscountInput = document.querySelector<HTMLInputElement>('input[formControlName="VendorDiscount"]');
    const commissionRateInput = document.querySelector<HTMLInputElement>('input[formControlName="CommissionRate"]');

    if (rateInput) rateInput.value = this.currencyPipe.transform(rate, '$', 'symbol', '1.2-2') || '$0.00';
    if (purchaseCostInput) purchaseCostInput.value = this.currencyPipe.transform(purchaseCost, '$', 'symbol', '1.2-2') || '$0.00';

    if (vendorDiscountInput) vendorDiscountInput.value =
      (vendorDiscount === 0 ? '0.00%' : this.decimalPipe.transform(vendorDiscount, '1.2-4') + '%');
    if (commissionRateInput) commissionRateInput.value =
      (commissionRate === 0 ? '0.00%' : this.decimalPipe.transform(commissionRate, '1.2-4') + '%');
  }

  toLocalDate(dateString: string): Date {
    const [y, M, d] = dateString.split('-').map(Number);
    return new Date(y, M - 1, d);
  }

  allowOnlyDigits(event: KeyboardEvent, maxDigits: number) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter'];

    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    // Limit max digits
    const input = event.target as HTMLInputElement;
    if (input.value.length >= maxDigits) {
      event.preventDefault();
    }
  }

  setDefaultIfEmpty(form: FormGroup, controlName: string, defaultValue: any = 0) {
    const control = form.get(controlName);
    if (!control) return;
    const value = control.value;
    if (value === null || value === undefined || value.toString().trim() === '') {
      control.setValue(defaultValue);
    }
  }

  onReorderBlur() {
    this.setDefaultIfEmpty(this.inventoryStockForm, 'ReorderPoint', 0);
  }

  onUsedLabelBlur() {
    this.setDefaultIfEmpty(this.barcodeTemplateForm, 'UsedLabel', 0);
  }

  onStockDetailsCancel(): void {
    this.isCollapsed = true;
    this.router.navigate(['/dashboard']);
  }

  printThermalBarcodeLabels(barcodePrintItems: string, labelType: string) {
    console.log(barcodePrintItems)
    const barcodePattern = /^(\d+:\d+)(,\d+:\d+)*$/;

    if (!barcodePattern.test(barcodePrintItems)) {
      console.error('Invalid barcode print format:', barcodePrintItems);
      return;
    }

    const validLabelTypes = ['standard', 'butterfly'];

    if (!validLabelTypes.includes(labelType?.toLowerCase())) {
      console.error('Invalid label type:', labelType);
      return;
    }

    const customProtocolUrl =
      'silentprinter://?methodname=inventorybarcodelabel' +
      '&param1=' + encodeURIComponent(barcodePrintItems) +
      '&param2=' + encodeURIComponent(labelType) +
      '&uniqueSessionId=' + this.uniqueSessionId;

    //console.log("Printing URL:", customProtocolUrl);

    window.location.href = customProtocolUrl;
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  public created(args1) {

    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('pencil')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('printBarcode')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('Rate')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('CurrentStock')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('PurchaseCost')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('ReorderPoint')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('CommissionRate')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('Count')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('VendorDiscount')].classList.add('e-hide');
    this.gridStockDetails.element.querySelectorAll('.e-filterdiv')[this.gridStockDetails.getColumnIndexByField('NoTax')].classList.add('e-hide');
    this.gridStockDetails.filterSettings = { mode: 'Immediate', immediateModeDelay: 100 };
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  ngOnDestroy(): void {
    this.editedCountMap = {};
    this.destroy$.next();
    this.destroy$.complete();
  }

}
